import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { FaFilePdf, FaFileExcel, FaTag, FaList, FaEnvelope } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import Loading from '~/Components/Loading';
import AuthStorage from '~/Utils/AuthStorage';
import { exportarEmExcel } from '~/Utils/Helpers';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  CARACTERISTICA_RELATORIO_CONTAS,
  CARACTERISTICA_ETIQUETA_AVULSA,
} from '~/constant';
import {
  buscarGruposEmpresaEtiquetas,
  buscarCidadesEtiquetas,
  buscarRamosEtiquetas,
  buscarRegioesEtiquetas,
  buscarRemetentesEtiquetas,
  emitirEtiquetas,
  emitirEtiquetasExcel,
  emitirCartas,
} from '~/Services/Service';
import FiltrosEmissao from './components/FiltrosEmissao';
import BlocoOcorrencia from './components/BlocoOcorrencia';
import ModalEtiquetaAvulsa from './components/ModalEtiquetaAvulsa';
import ModalSelecaoIndividual from './components/ModalSelecaoIndividual';
import UploadTemplateCarta from './components/UploadTemplateCarta';

// W-0604 — GES > Etiquetas / Cartas (migração TFEtiqueta).
// Emissão em lote de etiquetas (PDF/Excel) e cartas (blob) por tipo: comércio,
// fornecedor e remetente.  Avulsa e seleção individual protegidas
// pela característica 236 (RELATORIO_CONTAS) ou 240 (ETIQUETA_AVULSA).

const TIPOS_EMISSAO = [
  { value: 'comercio', label: 'Comércio' },
  { value: 'fornecedor', label: 'Fornecedor' },
  { value: 'remetente', label: 'Remetente' },
];

const TIPOS_CARTA = [
  { value: 'fornecedor', label: 'Carta Fornecedor' },
  { value: 'comercio', label: 'Carta Comércio' },
];

const FILTROS_INICIAIS = {
  codigo_grupo_empresa: null,
  codigo_cidade: null,
  codigo_ramo: null,
  codigo_regiao: null,
  conta_inicial: null,
  conta_final: null,
  aniver_dia_ini: null,
  aniver_dia_fim: null,
  aniver_mes_ini: null,
  aniver_mes_fim: null,
  imoveis_rescindidos: false,
  todos_clientes: false,
  quantidade_copias: 1,
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

const EtiquetasCartas = () => {
  const gepadrao = useSelector((state) => state.auth.user.gepadrao);

  // Lookups
  const [opcoesGE, setOpcoesGE] = useState([]);
  const [opcoesCidades, setOpcoesCidades] = useState([]);
  const [opcoesRamos, setOpcoesRamos] = useState([]);
  const [opcoesRegioes, setOpcoesRegioes] = useState([]);
  const [remetentes, setRemetentes] = useState([]);
  const [codigoRemetente, setCodigoRemetente] = useState(null);

  // Seleção de GE (controlled separado dos filtros para a cascade de cidades)
  const [geSelected, setGeSelected] = useState(null);

  // Estado principal
  const [tipoEmissao, setTipoEmissao] = useState('comercio');
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  const [ocorrencia, setOcorrencia] = useState({ ativa: false, titulo: '', texto: '' });
  const [selecionados, setSelecionados] = useState([]);

  // Cartas
  const [tipoCarta, setTipoCarta] = useState(null);

  // Modais
  const [showAvulsa, setShowAvulsa] = useState(false);
  const [showSelecao, setShowSelecao] = useState(false);

  // Loading / anti-duplo-envio
  const [buscando, setBuscando] = useState(false);
  const [emitindo, setEmitindo] = useState(false);
  const [emitindoExcel, setEmitindoExcel] = useState(false);
  const [emitindoCarta, setEmitindoCarta] = useState(false);

  const podeAvulsaOuSelecao =
    AuthStorage.temCaracteristica(CARACTERISTICA_RELATORIO_CONTAS) ||
    AuthStorage.temCaracteristica(CARACTERISTICA_ETIQUETA_AVULSA);

  // Carregamento inicial de lookups
  useEffect(() => {
    let ativo = true;

    buscarGruposEmpresaEtiquetas()
      .then((res) => {
        if (!ativo) return;
        const lista = res || [];
        const opcoes = lista.map((g) => ({
          value: g.codigo_grupo_empresa,
          label: g.apelido,
        }));
        setOpcoesGE(opcoes);

        // Auto-seleção: única opção ou gepadrao do Redux
        let inicial = null;
        if (opcoes.length === 1) {
          inicial = opcoes[0];
        } else if (gepadrao) {
          const encontrado = opcoes.find((o) => o.value === gepadrao);
          if (encontrado) inicial = encontrado;
        }
        if (inicial) {
          setGeSelected(inicial);
          setFiltros((prev) => ({ ...prev, codigo_grupo_empresa: inicial.value }));
        }
      })
      .catch((err) => {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar grupos de empresa.';
        toast.error(msg);
      });

    buscarRamosEtiquetas()
      .then((res) => {
        if (!ativo) return;
        setOpcoesRamos(
          (res || []).map((r) => ({ value: r.codigo_ramo, label: r.descricao_ramo }))
        );
      })
      .catch(() => {
        if (ativo) toast.error('Erro ao carregar ramos.');
      });

    buscarRegioesEtiquetas()
      .then((res) => {
        if (!ativo) return;
        setOpcoesRegioes(
          (res || []).map((r) => ({ value: r.codigo_regiao, label: r.descricao_regiao }))
        );
      })
      .catch(() => {
        if (ativo) toast.error('Erro ao carregar regiões.');
      });

    buscarRemetentesEtiquetas()
      .then((res) => {
        if (!ativo) return;
        setRemetentes(res || []);
      })
      .catch(() => {
        if (ativo) toast.error('Erro ao carregar remetentes.');
      });

    return () => {
      ativo = false;
    };
  }, [gepadrao]);

  // Cascade cidades quando GE muda
  useEffect(() => {
    const codigo = filtros.codigo_grupo_empresa;
    if (!codigo) {
      setOpcoesCidades([]);
      return undefined;
    }

    let ativo = true;
    buscarCidadesEtiquetas(codigo)
      .then((res) => {
        if (!ativo) return;
        setOpcoesCidades(
          (res || []).map((c) => ({ value: c.codigo_cidade, label: c.descricao_cidade }))
        );
      })
      .catch(() => {
        if (ativo) {
          setOpcoesCidades([]);
          toast.error('Erro ao carregar cidades.');
        }
      });

    return () => {
      ativo = false;
    };
  }, [filtros.codigo_grupo_empresa]);

  const handleGeChange = useCallback((opcao) => {
    setGeSelected(opcao);
    setFiltros((prev) => ({
      ...prev,
      codigo_grupo_empresa: opcao ? opcao.value : null,
      codigo_cidade: null,
    }));
    setOpcoesCidades([]);
  }, []);

  const handleTipoEmissaoChange = (tipo) => {
    setTipoEmissao(tipo);
    // Resetar filtros específicos ao trocar tipo
    setFiltros((prev) => ({
      ...prev,
      codigo_cidade: null,
      codigo_ramo: null,
      codigo_regiao: null,
      conta_inicial: null,
      conta_final: null,
      aniver_dia_ini: null,
      aniver_dia_fim: null,
      aniver_mes_ini: null,
      aniver_mes_fim: null,
      imoveis_rescindidos: false,
      todos_clientes: false,
    }));
    setSelecionados([]);
  };

  const buildPayload = () => {
    const isRemetente = tipoEmissao === 'remetente';
    return {
      tipo_emissao: tipoEmissao,
      codigo_remetente: isRemetente ? codigoRemetente : null,
      codigo_grupo_empresa: filtros.codigo_grupo_empresa,
      codigo_cidade: isRemetente ? null : filtros.codigo_cidade,
      codigo_ramo: isRemetente ? null : filtros.codigo_ramo,
      codigo_regiao: isRemetente ? null : filtros.codigo_regiao,
      conta_inicial: isRemetente ? null : filtros.conta_inicial,
      conta_final: isRemetente ? null : filtros.conta_final,
      aniver_dia_ini: isRemetente ? null : filtros.aniver_dia_ini,
      aniver_dia_fim: isRemetente ? null : filtros.aniver_dia_fim,
      aniver_mes_ini: isRemetente ? null : filtros.aniver_mes_ini,
      aniver_mes_fim: isRemetente ? null : filtros.aniver_mes_fim,
      imoveis_rescindidos: isRemetente ? false : filtros.imoveis_rescindidos,
      todos_clientes: isRemetente ? false : filtros.todos_clientes,
      quantidade_copias: filtros.quantidade_copias || 1,
      ocorrencia_ativa: ocorrencia.ativa,
      ocorrencia_titulo: ocorrencia.ativa ? ocorrencia.titulo : null,
      ocorrencia_texto: ocorrencia.ativa ? ocorrencia.texto : null,
    };
  };

  const handleEmitir = async () => {
    if (emitindo) return;
    setEmitindo(true);
    setBuscando(true);
    try {
      const payload = { ...buildPayload(), formato: 'etiqueta-pdf' };
      const blob = await emitirEtiquetas(payload);
      downloadBlob(blob, 'etiquetas.pdf');
      toast.success('Etiquetas emitidas com sucesso.');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao emitir etiquetas.';
      toast.error(msg);
    } finally {
      setEmitindo(false);
      setBuscando(false);
    }
  };

  const handleEmitirExcel = async () => {
    if (emitindoExcel) return;
    setEmitindoExcel(true);
    try {
      const payload = { ...buildPayload(), formato: 'excel' };
      const res = await emitirEtiquetasExcel(payload);
      const itens = res && res.itens ? res.itens : [];
      const linhas = [
        [
          'Conta',
          'GE',
          'Nome',
          'Endereco',
          'Bairro',
          'CEP',
          'Cidade',
          'UF',
          'Contato',
          'Ramo',
        ],
        ...itens.map((item) => [
          item.codigo_conta || item.codigoConta || '',
          item.codigo_grupo_empresa || item.codigoGrupoEmpresa || '',
          item.razao_social || item.razaoSocial || '',
          item.endereco || '',
          item.bairro || '',
          item.cep || '',
          item.cidade || '',
          item.uf || '',
          item.contato || '',
          item.descricao_ramo || item.descricaoRamo || '',
        ]),
      ];
      exportarEmExcel(linhas, 'etiquetas.csv');
      toast.success('Excel exportado com sucesso.');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao exportar Excel.';
      toast.error(msg);
    } finally {
      setEmitindoExcel(false);
    }
  };

  const handleEmitirSelecionados = useCallback(async (itensSelecionados) => {
    if (emitindo) return;
    setEmitindo(true);
    setBuscando(true);
    try {
      const blob = await emitirEtiquetas({
        ...buildPayload(),
        // O backend decide o público pelo tipo_emissao (não pelo modo): tem que ser
        // 'selecionados' p/ usar a lista escolhida — senão emite o público inteiro do GE.
        tipo_emissao: 'selecionados',
        formato: 'etiqueta-pdf',
        modo: 'selecionados',
        itens: itensSelecionados,
      });
      downloadBlob(blob, 'etiquetas-selecionadas.pdf');
      toast.success('Etiquetas emitidas com sucesso.');
      setShowSelecao(false);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao emitir etiquetas selecionadas.';
      toast.error(msg);
    } finally {
      setEmitindo(false);
      setBuscando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitindo, filtros, ocorrencia, tipoEmissao]);

  const handleEmitirCarta = useCallback(async (formData) => {
    if (emitindoCarta) return;
    setEmitindoCarta(true);
    setBuscando(true);
    try {
      const blob = await emitirCartas(formData);
      downloadBlob(blob, `carta-${tipoCarta}.pdf`);
      toast.success('Carta emitida com sucesso.');
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao emitir carta.';
      toast.error(msg);
    } finally {
      setEmitindoCarta(false);
      setBuscando(false);
    }
  }, [emitindoCarta, tipoCarta]);

  const handleTipoCarta = (tipo) => setTipoCarta((prev) => (prev === tipo ? null : tipo));

  const filtrosParaSelecao = {
    tipo_emissao: tipoEmissao,
    codigo_grupo_empresa: filtros.codigo_grupo_empresa,
    codigo_cidade: filtros.codigo_cidade,
    codigo_ramo: filtros.codigo_ramo,
    codigo_regiao: filtros.codigo_regiao,
  };

  return (
    <>
      <Header title="Etiquetas / Cartas" />

      <Container fluid className="py-3">
        {/* Card principal: tipo de emissão + filtros + ações */}
        <Card className="mb-3">
          <Card.Header className="font-weight-bold">Tipo de Emissão</Card.Header>
          <Card.Body>
            {/* Radios tipo de emissão */}
            <Row className="mb-3">
              <Col xs={12}>
                {TIPOS_EMISSAO.map((t) => (
                  <Form.Check
                    key={t.value}
                    inline
                    type="radio"
                    id={`tipo-emissao-${t.value}`}
                    label={t.label}
                    checked={tipoEmissao === t.value}
                    onChange={() => handleTipoEmissaoChange(t.value)}
                  />
                ))}
              </Col>
            </Row>

            {/* Seleção de Grupo de Empresa */}
            <Row className="mb-3">
              <Col xs={12} md={5} lg={4}>
                <Form.Label className="font-weight-bold">
                  Grupo de Empresa
                </Form.Label>
                <Select
                  placeholder="Selecione o GE..."
                  isClearable
                  options={opcoesGE}
                  value={geSelected}
                  onChange={handleGeChange}
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  noOptionsMessage={() => 'Nenhum GE encontrado'}
                />
              </Col>
            </Row>

            {/* Filtros específicos por tipo (delegado ao sub-componente) */}
            {tipoEmissao !== 'remetente' && (
              <FiltrosEmissao
                filtros={filtros}
                tipoEmissao={tipoEmissao}
                opcoesCidades={opcoesCidades}
                opcoesRamos={opcoesRamos}
                opcoesRegioes={opcoesRegioes}
                onChange={setFiltros}
              />
            )}

            {/* Remetente: seletor + quantidade de cópias (Fonte: UEtiqueta.pas:825 BitBtn2) */}
            {tipoEmissao === 'remetente' && (
              <Row className="mb-3">
                <Col xs={12} md={6} lg={5}>
                  <Form.Label className="font-weight-bold">Remetente</Form.Label>
                  <Select
                    options={remetentes.map((r) => ({
                      value: r.codigo,
                      label: `${r.codigo} - ${r.nome}`,
                    }))}
                    value={
                      codigoRemetente == null
                        ? null
                        : (() => {
                            const r = remetentes.find((x) => x.codigo === codigoRemetente);
                            return r ? { value: r.codigo, label: `${r.codigo} - ${r.nome}` } : null;
                          })()
                    }
                    onChange={(opt) => setCodigoRemetente(opt ? opt.value : null)}
                    placeholder="Selecione o remetente..."
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    isClearable
                    noOptionsMessage={() => 'Nenhum remetente cadastrado'}
                  />
                </Col>
                <Col xs={6} md={3} lg={2}>
                  <Form.Label className="font-weight-bold">
                    Quantidade de Cópias
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={99}
                    value={filtros.quantidade_copias}
                    onChange={(e) =>
                      setFiltros((prev) => ({
                        ...prev,
                        quantidade_copias: parseInt(e.target.value, 10) || 1,
                      }))
                    }
                    size="sm"
                  />
                </Col>
              </Row>
            )}

            {/* Bloco de ocorrência */}
            <BlocoOcorrencia
              ocorrencia={ocorrencia}
              onOcorrenciaChange={setOcorrencia}
            />

            {/* Botões de ação */}
            <Row className="mt-3">
              <Col xs={12}>
                <Button
                  variant="primary"
                  className="mr-2 mb-2"
                  onClick={handleEmitir}
                  disabled={emitindo || emitindoExcel}
                >
                  {emitindo ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Emitindo...
                    </>
                  ) : (
                    <>
                      <FaFilePdf className="mr-1" />
                      Emitir PDF
                    </>
                  )}
                </Button>

                <Button
                  variant="success"
                  className="mr-2 mb-2"
                  onClick={handleEmitirExcel}
                  disabled={emitindo || emitindoExcel}
                >
                  {emitindoExcel ? (
                    <>
                      <Spinner animation="border" size="sm" className="mr-1" />
                      Exportando...
                    </>
                  ) : (
                    <>
                      <FaFileExcel className="mr-1" />
                      Emitir Excel
                    </>
                  )}
                </Button>

                {podeAvulsaOuSelecao && (
                  <>
                    <Button
                      variant="outline-secondary"
                      className="mr-2 mb-2"
                      onClick={() => setShowAvulsa(true)}
                      disabled={emitindo || emitindoExcel}
                    >
                      <FaTag className="mr-1" />
                      Avulsa
                    </Button>

                    <Button
                      variant="outline-secondary"
                      className="mb-2"
                      onClick={() => setShowSelecao(true)}
                      disabled={emitindo || emitindoExcel}
                    >
                      <FaList className="mr-1" />
                      Selecionar
                    </Button>
                  </>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Card: Cartas */}
        <Card className="mb-3">
          <Card.Header className="font-weight-bold">Cartas</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col xs={12}>
                {TIPOS_CARTA.map((tc) => (
                  <Button
                    key={tc.value}
                    variant={tipoCarta === tc.value ? 'primary' : 'outline-primary'}
                    className="mr-2 mb-2"
                    onClick={() => handleTipoCarta(tc.value)}
                    disabled={emitindoCarta}
                  >
                    <FaEnvelope className="mr-1" />
                    {tc.label}
                  </Button>
                ))}
              </Col>
            </Row>

            {tipoCarta && (
              <UploadTemplateCarta
                tipoCarta={tipoCarta}
                codigoGrupoEmpresa={filtros.codigo_grupo_empresa}
                filtros={filtros}
                ocorrencia={ocorrencia}
                emitindo={emitindoCarta}
                onEmitir={handleEmitirCarta}
              />
            )}
          </Card.Body>
        </Card>

        {buscando && <Loading />}
      </Container>

      {/* Modal: Etiqueta Avulsa */}
      <ModalEtiquetaAvulsa
        show={showAvulsa}
        onHide={() => setShowAvulsa(false)}
        gruposEmpresa={opcoesGE}
        codigoGrupoEmpresaDefault={filtros.codigo_grupo_empresa}
        remetentes={remetentes}
      />

      {/* Modal: Seleção Individual */}
      <ModalSelecaoIndividual
        show={showSelecao}
        onHide={() => setShowSelecao(false)}
        filtros={filtrosParaSelecao}
        selecionados={selecionados}
        onSelecionadosChange={setSelecionados}
        gruposEmpresa={opcoesGE}
        cidades={opcoesCidades}
        ramos={opcoesRamos}
        regioes={opcoesRegioes}
        onEmitir={handleEmitirSelecionados}
        emitindo={emitindo}
      />
    </>
  );
};

export default EtiquetasCartas;
